import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import { Link, useLocation, useNavigate } from "react-router-dom"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import { useSelector } from "react-redux"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useDispatch } from "react-redux"
import {
  getAllOrders,
  syncOrderWithPos,
  updateOrderStatus,
} from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import { getAllBranchs } from "store/actions/usersAction"
import Swal from "sweetalert2"
import {
  buildOrderExportPayload,
  canExportOrders,
  downloadOrderExport,
} from "utils/orderExportUtils"
import { downloadOrderDetailPdf } from "helpers/orderDetailPdf"
import { hasWalletRelatedPermission } from "utils/walletPermissionUtils"
import {
  getPerPageOptions,
  mobileOrderStatus,
  paymentMethodTypes,
} from "components/Common/constants"
import Select from "react-select"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import { setItemsPerPage } from "store/reducers/orderReducer"
import { getAppVersions } from "store/actions/customersAction"
import AssignToRiderModal from "components/Orders/AssignToRiderModal"
import AssignToBranchModal from "components/Orders/AssignToBranchModal"
import {
  hasExternalPosIntegration,
  isFnbPosProvider,
  isFoodicsProvider,
} from "utils/orderPosUtils"
import {
  getSystemOrderStatusLabel,
  getSystemStatusDisplay,
  getSystemStatusFilterOptions,
  getSyrvePosStatusFilterOptions,
  getFoodicsPosStatusFilterOptions,
  getPosStatusLabel,
  getPosStatusColor,
  resolveSystemOrderStatusValue,
} from "utils/orderStatusUtils"
import { Input } from "reactstrap"
import formatDisplayDateTime from "components/Shared/formatDateTime"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { useAssignedBrands } from "hooks/useAssignedBrands"
import { brandShowsScheduledDeliveryDateInOrders } from "utils/deliveryProfileHelpers"
import { getAllowedOrderTypeFilterOptions } from "utils/fulfillmentPermissions"

const formatFilterDateTime = date =>
  date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "")

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatFilterDateTime(startOfDay),
    max: formatFilterDateTime(endOfDay),
  }
}

const ORDER_STATUS_SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  menuPlacement: "auto",
  menuShouldScrollIntoView: false,
}

const orderStatusSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
}

const Orders = ({ brandConfig = null }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const ordersBasePath = brandConfig
    ? `/orders/${brandConfig.brandId}`
    : "/f&b-orders"
  // Legacy F&B route has no brandConfig — treat as Syrve POS.
  // Brand-scoped pages: only Syrve/Salasa count as external POS.
  const hasPos = brandConfig
    ? hasExternalPosIntegration(brandConfig)
    : true
  const serviceProvider = hasPos
    ? brandConfig?.serviceProvider || "Syrve"
    : ""
  // Orders.brandId is stored as string — keep filter payload as string
  const brandId =
    brandConfig?.brandId != null && String(brandConfig.brandId).trim() !== ""
      ? String(brandConfig.brandId).trim()
      : ""
  const activeOrderStatusMap = mobileOrderStatus
  const isSyrve = hasPos && (isFnbPosProvider(brandConfig) || !brandConfig)
  const isFoodicsBrand = isFoodicsProvider(brandConfig)
  const resolveListStatus = status =>
    !hasPos && Number(status) === 6 ? 0 : Number(status) || 0
  const { hasOnlyOneBrand } = useAssignedBrands()
  const showScheduledDeliveryDate =
    brandShowsScheduledDeliveryDateInOrders(brandConfig)
  const pageTitle = hasOnlyOneBrand
    ? t("Orders")
    : brandConfig?.displayName ||
      brandConfig?.brandName ||
      t("F&B Orders")

  document.title = `${pageTitle} `

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const orderTypeParam = searchParams.get("orderType")
  const assignmentStatusParam = searchParams.get("assignmentStatus")
  const platformParam = searchParams.get("platform")
  const paymentMethodParam = searchParams.get("paymentMethod")
  const paymentStatusParam = searchParams.get("paymentStatus")
  const minDateParam = searchParams.get("minDate")
  const maxDateParam = searchParams.get("maxDate")
  const posStatusParam = searchParams.get("posStatus") || "0"

  const {
    loading,
    isLoading,
    isUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)
  const { branchsList } = useSelector(state => state.Users)
  const { appVersions } = useSelector(state => state.Customers)
  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const branchSubPermissions = getSubPermissions(
    userRole,
    5,
    "branchLocations_3",
    accountType,
    accountScope
  )
  const orderTypeFilterOptions = getAllowedOrderTypeFilterOptions(
    branchSubPermissions
  ).map(option => ({
    ...option,
    label: t(option.label),
  }))
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [searchTerm2, setSearchTerm2] = useState("")

  // const [appVersion, setAppVersion] = useState("")

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    branchId: branchIdParam
      ? branchIdParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    status: parseInt(searchParams.get("status") || "0"),
    posStatus: parseInt(posStatusParam || "0"),
    orderType: orderTypeParam
      ? orderTypeParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    platform: platformParam
      ? platformParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    appVersion: "",
    assignmentStatus: assignmentStatusParam || "",
    paymentMethod: paymentMethodParam
      ? paymentMethodParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    paymentStatus: paymentStatusParam || "",
  })

  const [selectedOrderIds, setSelectedOrderIds] = useState([])
  const [showAssignRiderModal, setShowAssignRiderModal] = useState(false)
  const [showAssignBranchModal, setShowAssignBranchModal] = useState(false)

  const flatpickrRef = useRef(null)
  const estimatedDeliveryFlatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState(() =>
    minDateParam && maxDateParam
      ? { min: minDateParam, max: maxDateParam }
      : getDefaultDateRange()
  )
  const [estimatedDeliveryDateRange, setEstimatedDeliveryDateRange] =
    useState({})
  const [showModal, setShowModal] = useState(false)
  const [downloadingPdfId, setDownloadingPdfId] = useState(null)

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        // { label: "All", value: "" },
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "status",
      type: "select",
      options: [
        ...getSystemStatusFilterOptions(t),
        ...(isSyrve
          ? [{ label: t("Sent To Branch Failed"), value: 6 }]
          : []),
      ],
      placeholder: t("Select Status"),
    },
    ...(isSyrve
      ? [
          {
            name: "posStatus",
            type: "select",
            options: isFoodicsBrand
              ? getFoodicsPosStatusFilterOptions(t)
              : getSyrvePosStatusFilterOptions(t),
            placeholder: t("POS Status"),
          },
        ]
      : []),
    {
      name: "orderType",
      type: "multiSelect",
      options: orderTypeFilterOptions,
      placeholder: t("Select Order Type"),
    },
    {
      name: "assignmentStatus",
      type: "select",
      options: [
        { label: t("All Assignments"), value: "" },
        { label: t("Assigned to Rider"), value: "assigned" },
        { label: t("Not Assigned"), value: "unassigned" },
      ],
      placeholder: t("Rider Assignment"),
    },
    {
      name: "platform",
      type: "multiSelect",
      options: [
        { label: t("Android"), value: "android" },
        { label: t("iOS"), value: "iOS" },
        { label: t("Website"), value: "website" },
      ],
      placeholder: t("Platform"),
    },
    {
      name: "paymentMethod",
      type: "multiSelect",
      options: paymentMethodTypes.map(item => ({
        label: t(item.label),
        value: item.value,
      })),
      placeholder: t("Payment Method"),
    },
    {
      name: "paymentStatus",
      type: "select",
      options: [
        { label: t("All"), value: "" },
        { label: t("Pending"), value: 0 },
        { label: t("Paid"), value: 1 },
        { label: t("Refunded"), value: 2 },
      ],
      placeholder: t("Payment Status"),
    },
  ]

  if (
    selectedFilters?.platform?.some(
      item => item.value === "android" || item.value === "iOS"
    )
  ) {
    filters.push({
      name: "appVersion",
      type: "select",
      options: appVersions.map(item => ({
        label: item,
        value: item,
      })),
      placeholder: t("App Versions"),
    })
  }

  useEffect(() => {
    fetchAllOrders()
    fetchAllBranchs()
  }, [])

  const fetchAllOrders = () => {
    const {
      status,
      posStatus,
      sortOrder,
      branchId,
      orderType,
      platform,
      appVersion,
      assignmentStatus,
      paymentMethod,
      paymentStatus,
    } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPaymentMethod = paymentMethod?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item => {
        if (item.value === "android") return [item.value, "Android"]
        if (item.value === "website") return [item.value, "Website"]
        return item.value
      }) || []
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      orderNo: searchTerm2 || "",
      appVersion: platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
        ? appVersion
        : "",
      status: resolveListStatus(status),
      posStatus: posStatus && Number(posStatus) !== 0 ? Number(posStatus) : undefined,
      branchId: branchIds,
      orderType: updatedOrderType,
      assignmentStatus: assignmentStatus || "",
      platform: updatedPlatform,
      paymentMethod: updatedPaymentMethod,
      paymentStatus:
        paymentStatus === "" || paymentStatus == null
          ? undefined
          : Number(paymentStatus),
      serviceProvider,
      brandId,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      estimatedDeliveryDateRange:
        showScheduledDeliveryDate &&
        estimatedDeliveryDateRange.min &&
        estimatedDeliveryDateRange.max
          ? {
              min: estimatedDeliveryDateRange.min,
              max: estimatedDeliveryDateRange.max,
            }
          : {},
    }
    dispatch(getAllOrders(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    const { platform } = selectedFilters
    if (
      platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
    ) {
      fetchAppVersions()
    }
  }, [selectedFilters])

  const fetchAppVersions = () => {
    const { platform } = selectedFilters
    const updatedPlatform =
      platform
        ?.filter(item => item.value === "android" || item.value === "iOS")
        .map(item => item.value) || []
    const transformedPlatform = updatedPlatform?.map(item =>
      item === "android" ? "Android" : item
    )
    const payload = { platform: transformedPlatform }
    dispatch(getAppVersions(payload))
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
    assignToRider: null,
    assignToBranch: null,
    export: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = getSubPermissions(
      userRole,
      9,
      "order_1",
      accountType
    )
    const isPermissionEdit = getSubPermissions(
      userRole,
      9,
      "order_2",
      accountType
    )
    const isPermissionAssign = getSubPermissions(
      userRole,
      9,
      "order_3",
      accountType
    )
    const canViewOrders = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )

    if (!canViewOrders) {
      navigateToAccessibleRoute(navigate, { userRole, accountType })
      return
    }
    setUsersPermissions({
      list: canViewOrders,
      edit:
        isPermissionEnabled(userRole, 9, "order_2", accountType) ||
        isPermissionEdit?.update_orders?.isEnabled,
      assignToRider:
        isTenantOwnerAdmin() ||
        isPermissionAssign?.assign_orders_to_rider?.isEnabled,
      assignToBranch:
        isTenantOwnerAdmin() ||
        isPermissionAssign?.assign_orders_to_branch?.isEnabled,
      export: canExportOrders({
        userRole,
        accountType,
      }),
    })
  }, [userRole])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const handleAppVersionChange = e => {
    // setAppVersion(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && selectedValue.length === 0) {
        updatedFilters.appVersion = "" // Reset appVersion if platform is cleared
      }

      return updatedFilters
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const handleFetchOrders = (page = 1) => {
    const {
      status,
      posStatus,
      sortOrder,
      branchId,
      orderType,
      platform,
      appVersion,
      assignmentStatus,
      paymentMethod,
      paymentStatus,
    } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPaymentMethod = paymentMethod?.map(item => item.value) || []
    // const updatedPlatform = platform?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item => {
        if (item.value === "android") return [item.value, "Android"]
        if (item.value === "website") return [item.value, "Website"]
        return item.value
      }) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }
    if (orderType?.length > 0) {
      const orderTypeString = orderType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("orderType", orderTypeString)
    }
    if (platform?.length > 0) {
      const platformString = platform
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("platform", platformString)
    }
    if (assignmentStatus) {
      searchParams.set("assignmentStatus", assignmentStatus)
    }
    if (paymentMethod?.length > 0) {
      const paymentMethodString = paymentMethod
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("paymentMethod", paymentMethodString)
    }
    if (paymentStatus !== "" && paymentStatus != null) {
      searchParams.set("paymentStatus", String(paymentStatus))
    }
    if (posStatus && Number(posStatus) !== 0) {
      searchParams.set("posStatus", String(posStatus))
    }

    const hasDateRange = dateRange?.min && dateRange?.max

    if (hasDateRange) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      appVersion: platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
        ? appVersion
        : "",
      branchId: branchIds,
      status: resolveListStatus(status),
      posStatus: posStatus && Number(posStatus) !== 0 ? Number(posStatus) : undefined,
      orderType: updatedOrderType,
      assignmentStatus: assignmentStatus || "",
      platform: updatedPlatform,
      paymentMethod: updatedPaymentMethod,
      paymentStatus:
        paymentStatus === "" || paymentStatus == null
          ? undefined
          : Number(paymentStatus),
      serviceProvider,
      brandId,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
      estimatedDeliveryDateRange:
        showScheduledDeliveryDate &&
        estimatedDeliveryDateRange.min &&
        estimatedDeliveryDateRange.max
          ? {
              min: estimatedDeliveryDateRange.min,
              max: estimatedDeliveryDateRange.max,
            }
          : {},
    }

    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`${ordersBasePath}?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchOrders(1)
  }

  const onPageChange = page => {
    handleFetchOrders(page)
  }

  // useEffect(() => {
  //   const { platform } = selectedFilters
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleDelete = () => {
    setShowDeleteModal(false)
  }

  const handleReset = () => {
    dispatch(setItemsPerPage(50))

    const resetDateRange = getDefaultDateRange()
    const payload = {
      perPage: 50,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      status: 0,
      dateRange: resetDateRange,
      estimatedDeliveryDateRange: {},
      serviceProvider,
      brandId,
    }
    dispatch(getAllOrders(payload))
    setSelectedFilters({
      sortOrder: 2,
      branchId: [],
      status: 0,
      posStatus: 0,
      orderType: [],
      platform: [],
      appVersion: "",
      assignmentStatus: "",
      paymentMethod: [],
      paymentStatus: "",
    })
    setSelectedOrderIds([])
    // setAppVersion("")
    setSearchTerm("")
    setSearchTerm2("")
    setDateRange(resetDateRange)
    setEstimatedDeliveryDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [resetDateRange.min, resetDateRange.max],
        true
      )
    }
    if (estimatedDeliveryFlatpickrRef.current) {
      estimatedDeliveryFlatpickrRef.current.flatpickr.clear()
    }
    navigate(`${ordersBasePath}?page=1`)
  }

  const handleRowClick = id => {
    navigate(`${ordersBasePath}/order-details?id=${id}`)
  }

  const getExportFileName = (suffix = "") => {
    const hasDateRange = dateRange?.min && dateRange?.max
    if (selectedOrderIds.length > 0) {
      return t("Selected F&B Orders{{suffix}}", {
        suffix: suffix ? ` (${suffix.toUpperCase()})` : "",
      })
    }
    if (hasDateRange) {
      return t(`f&b_order_from_${dateRange.min}_to_${dateRange.max}`)
    }
    return t("F&B Orders Till Date")
  }

  const runOrderExport = async exportType => {
    const payload = buildOrderExportPayload({
      exportType,
      selectedFilters,
      searchTerm,
      searchTerm2,
      dateRange,
      estimatedDeliveryDateRange,
      serviceProvider,
      includeServiceProvider: hasPos,
      includePosColumns: isSyrve,
      includeSalasaColumns: false,
      brandId,
      orderIds: selectedOrderIds,
      resolveListStatus,
    })

    await downloadOrderExport({
      dispatch,
      t,
      payload,
      fileName: getExportFileName(exportType === "xlsx" ? "xlsx" : "csv"),
    })
  }

  const handleExcelDownload = async () => {
    await runOrderExport("xlsx")
  }

  const exportToCSV = async () => {
    await runOrderExport("csv")
  }

  const handleExportSelected = async exportType => {
    if (!selectedOrderIds.length) return
    await runOrderExport(exportType)
  }

  // Convert bookingStatus to options array for react-select
  const statusOptions = Object.entries(activeOrderStatusMap).map(
    ([value, label]) => ({
      value,
      label: t(label),
    })
  )

  const handleStatusChange = async (id, status) => {
    const order = orders?.find(item => item._id === id)
    const nextStatus = Number(status)
    const isDeliveryOrder = order?.orderType === "delivery"
    // Rider app owns Out for Delivery (3) and Delivered (4) for delivery orders
    if (isDeliveryOrder && (nextStatus === 3 || nextStatus === 4)) {
      dispatch(
        showAlert({
          text: t(
            "Out for delivery and delivered statuses are updated by the rider app"
          ),
          type: "error",
        })
      )
      return
    }

    const confirm = await Swal.fire({
      title: t("Are you sure?"),
      text: t("Do you want to update this order status?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Yes"),
      cancelButtonText: t("Cancel"),
      reverseButtons: true,
    })
    if (!confirm.isConfirmed) {
      return
    }

    setSelectedId(id)
    const payload = {
      id,
      status,
    }
    dispatch(updateOrderStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleSentOnPOS = () => {
    const payload = {
      orderId: selectedId,
    }
    dispatch(syncOrderWithPos(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
          handleFetchOrders()
          setShowModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  const handleDownloadOrderPdf = async (orderId, e) => {
    e?.stopPropagation()
    e?.preventDefault()
    if (!orderId || downloadingPdfId) return
    setDownloadingPdfId(String(orderId))
    try {
      await downloadOrderDetailPdf(orderId, {
        includeWalletAmount: hasWalletRelatedPermission(
          userRole,
          accountType,
          accountScope
        ),
      })
    } catch (error) {
      dispatch(
        showAlert({
          text: error?.message || t("Download failed."),
          type: "error",
        })
      )
    } finally {
      setDownloadingPdfId(null)
    }
  }

  const getOrderId = order => String(order?._id || order?.id || "")

  const selectedOrders = orders.filter(o =>
    selectedOrderIds.includes(getOrderId(o))
  )
  const hasSelection = selectedOrderIds.length > 0
  const canAssignToRider =
    usersPermissions.assignToRider &&
    selectedOrders.length > 0 &&
    selectedOrders.every(o => o.orderType === "delivery")

  const toggleOrderSelection = (orderId, e) => {
    e?.stopPropagation()
    const id = String(orderId)
    if (!id) return
    setSelectedOrderIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = e => {
    e?.stopPropagation()
    const pageIds = orders.map(o => getOrderId(o)).filter(Boolean)
    const allSelected = pageIds.every(id => selectedOrderIds.includes(id))
    if (allSelected) {
      setSelectedOrderIds(prev => prev.filter(id => !pageIds.includes(id)))
    } else {
      setSelectedOrderIds(prev => [...new Set([...prev, ...pageIds])])
    }
  }

  const isAllOnPageSelected =
    orders.length > 0 &&
    orders.every(o => selectedOrderIds.includes(getOrderId(o)))

  const handleAssignSuccess = () => {
    setSelectedOrderIds([])
    handleFetchOrders(currentPage)
  }

  const deliveryStatusLabel = status => {
    const labels = {
      unassigned: t("Not Assigned"),
      assigned: t("Assigned"),
      in_progress: t("In Progress"),
      delivered: t("Delivered"),
      cancelled: t("Cancelled"),
    }
    return labels[status] || status || "-"
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                cardTitle={pageTitle}
                isAddBtn={false}
                isExportData={loading ? false : usersPermissions.export}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  dateRangeWithTime={true}
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  // isSecondSearch={false}
                  // appVersion={appVersion}
                  // handleAppVersionChange={handleAppVersionChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  pageSizes={[0, 1, 2]}
                  itemsPerPage={50}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Phone number")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Order No.")}
                  colSize={4}
                  isEstimatedDeliveryDateRange={showScheduledDeliveryDate}
                  estimatedDeliveryDateRange={estimatedDeliveryDateRange}
                  setEstimatedDeliveryDateRange={setEstimatedDeliveryDateRange}
                  estimatedDeliveryFlatpickrRef={estimatedDeliveryFlatpickrRef}
                />
                {hasSelection &&
                  (usersPermissions.assignToRider ||
                    usersPermissions.assignToBranch ||
                    usersPermissions.export) && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded">
                      <span className="text-muted me-2">
                        {t("{{count}} selected", {
                          count: selectedOrderIds.length,
                        })}
                      </span>
                      {usersPermissions.export && (
                        <>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleExportSelected("xlsx")}
                          >
                            {t("Export Selected (Excel)")}
                          </Button>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleExportSelected("csv")}
                          >
                            {t("Export Selected (CSV)")}
                          </Button>
                        </>
                      )}
                      {usersPermissions.assignToRider && (
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!canAssignToRider}
                          title={
                            !canAssignToRider
                              ? t("Only delivery orders can be assigned to a rider")
                              : ""
                          }
                          onClick={() => setShowAssignRiderModal(true)}
                        >
                          {t("Assign to Rider")}
                        </Button>
                      )}
                      {usersPermissions.assignToBranch && (
                        <Button
                          color="secondary"
                          size="sm"
                          onClick={() => setShowAssignBranchModal(true)}
                        >
                          {t("Assign to Branch")}
                        </Button>
                      )}
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => setSelectedOrderIds([])}
                      >
                        {t("Clear selection")}
                      </Button>
                    </div>
                  )}
                {/* )} */}
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : orders.length === 0 ? (
                    <div className="g_center" style={{ minHeight: 200 }}>
                      <p className="text-center my-2 mb-2">
                        {t("No orders found")}
                      </p>
                    </div>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {(usersPermissions.assignToRider ||
                            usersPermissions.assignToBranch ||
                            usersPermissions.export) && (
                            <th
                              style={{ width: 40 }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input cursor-pointer"
                                checked={isAllOnPageSelected}
                                onClick={toggleSelectAllOnPage}
                                aria-label={t("Select all orders on page")}
                              />
                            </th>
                          )}
                          <th style={{ whiteSpace: "nowrap" }}>
                            {t("S. No.")}
                          </th>
                          <th>{t("Order No")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          {/* <th>Email</th> */}
                          <th>{t("Platform")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Order Type")}</th>
                          {showScheduledDeliveryDate ? (
                            <th>{t("Est. Delivery Date")}</th>
                          ) : null}
                          <th>{t("Branch Name")}</th>
                          <th>{t("Rider")}</th>
                          <th>{t("Rider Assignment")}</th>
                          {hasPos && <th>{t("Service Provider")}</th>}
                          <th>{t("Date & Time")}</th>
                          <th>{t("Payment Method")}</th>
                          <th>{t("Status")}</th>
                          {isSyrve && <th>{t("POS Status")}</th>}
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 200 }}>
                              {t("Update Status")}
                            </th>
                          )}
                          <th style={{ width: 56, textAlign: "center" }}>
                            {t("Download")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              handleRowClick(item._id)
                            }}
                            className="cursor-pointer"
                          >
                            {(usersPermissions.assignToRider ||
                              usersPermissions.assignToBranch ||
                              usersPermissions.export) && (
                              <td
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  checked={selectedOrderIds.includes(
                                    getOrderId(item)
                                  )}
                                  onClick={e =>
                                    toggleOrderSelection(getOrderId(item), e)
                                  }
                                  aria-label={t("Select order")}
                                />
                              </td>
                            )}
                            <th>
                              {" "}
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            {/* <th scope="row">
                              <Link
                                className="text-decoration-underline gray_text"
                                to={`/orders/order-details?id=${item._id}`}
                              >
                                {item._id}
                              </Link>
                            </th> */}
                            <td>{item.invoiceNo}</td>
                            <td>{item?.name || "-"}</td>
                            <td>
                              {item?.phone ? (
                                <span
                                  dir="ltr"
                                  style={{ unicodeBidi: "bidi-override" }}
                                >
                                  {item.phone}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>{" "}
                            {/* <td>{item.email || "-"}</td> */}
                            <td key={language}>
                              {item.platform === "android" ||
                              item.platform === "Android"
                                ? t("Android")
                                : item.platform === "iOS"
                                ? t("iOS")
                                : item.platform === "website" ||
                                  item.platform === "Website"
                                ? t("Website")
                                : item.platform
                                ? t(item.platform)
                                : "-"}
                              {(item.platform === "android" ||
                                item.platform === "Android" ||
                                item.platform === "iOS") &&
                                item?.appVersion &&
                                ` (${item.appVersion})`}
                            </td>
                            <td>
                              {item.paidAmount} {item?.splitPayments?.currency}
                            </td>
                            <td className="text-capitalize">
                              {t(item.orderType)}
                            </td>
                            {showScheduledDeliveryDate ? (
                              <td>
                                {item.orderType === "delivery" &&
                                item.estimatedDeliveryDate
                                  ? formatDisplayDateTime(
                                      item.estimatedDeliveryDate
                                    )
                                  : "-"}
                              </td>
                            ) : null}
                            <td className="text-capitalize">
                              {language === "sa"
                                ? item?.branchNameAr
                                : item?.branchName}
                            </td>
                            <td>
                              {item.orderType === "delivery"
                                ? item.deliveryPartnerName || "-"
                                : "-"}
                            </td>
                            <td>
                              {item.orderType === "delivery"
                                ? deliveryStatusLabel(item.deliveryStatus)
                                : "-"}
                            </td>
                            {hasPos && (
                              <td className="text-capitalize">
                                {t(item.serviceProvider)}
                              </td>
                            )}
                            <td>{formatDisplayDateTime(item.createdAt)}</td>
                            <td>
                              <i
                                className={`${
                                  item.paymentMethod === "CARD"
                                    ? "fab fa-cc-mastercard me-1"
                                    : "" || item.paymentMethod === "APPLE"
                                    ? "fab bx bxl-apple me-1"
                                    : ""
                                }`}
                              ></i>
                              {t(item.paymentMethod)}
                            </td>
                            <td>
                              {/* System status — orderType-aware labels for 3/4 */}
                              {(() => {
                                const display = getSystemStatusDisplay(
                                  {
                                    ...item,
                                    brandIntegration:
                                      brandConfig?.integration ||
                                      item.serviceProvider,
                                  },
                                  t
                                )
                                return (
                                  <span
                                    style={{
                                      color: display.color,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {display.text}
                                  </span>
                                )
                              })()}
                            </td>
                            {isSyrve && (
                              <td>
                                <span
                                  style={{
                                    color: getPosStatusColor({
                                      ...item,
                                      brandIntegration:
                                        brandConfig?.integration || "Syrve",
                                    }),
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getPosStatusLabel(
                                    {
                                      ...item,
                                      brandIntegration:
                                        brandConfig?.integration || "Syrve",
                                    },
                                    t
                                  )}
                                </span>
                              </td>
                            )}
                            {/* <td>
                              {usersPermissions.list && (
                                <Button
                                  type="button"
                                  color="primary"
                                  className="btn-sm btn-rounded"
                                  onClick={() => {
                                    navigate(
                                      `/orders/order-details?id=${item._id}`
                                    )
                                  }}
                                >
                                  View Details
                                </Button>
                              )}
                            </td> */}
                            {usersPermissions.edit && (
                              <th onClick={e => e.stopPropagation()}>
                                {hasPos &&
                                item?.posSettings?.status === "Error" ? (
                                  <Button
                                    color="primary"
                                    title={t("Sync Order With POS")}
                                    onClick={() => {
                                      setShowModal(true)
                                      setSelectedId(item._id)
                                    }}
                                  >
                                    {t("Send on POS")}
                                  </Button>
                                ) : (
                                  <Select
                                    value={{
                                      value: resolveSystemOrderStatusValue(
                                        item
                                      ).toString(),
                                      label: getSystemOrderStatusLabel(
                                        resolveSystemOrderStatusValue(item),
                                        item.orderType,
                                        t
                                      ),
                                    }}
                                    isDisabled={
                                      selectedId === item._id && isLoading
                                    }
                                    onChange={(selectedOption, event) => {
                                      if (event.nativeEvent) {
                                        event.nativeEvent.stopPropagation()
                                      }
                                      handleStatusChange(
                                        item._id,
                                        selectedOption.value
                                      )
                                    }}
                                    options={statusOptions
                                      .filter(option => {
                                        const currentStatus =
                                          resolveSystemOrderStatusValue(item)
                                        const optionValue = parseInt(
                                          option.value
                                        )
                                        const isDelivery =
                                          item.orderType === "delivery"

                                        switch (currentStatus) {
                                          case 1:
                                            return (
                                              optionValue === 2 ||
                                              optionValue === 5
                                            )
                                          case 2:
                                            // Delivery: Out for Delivery (3) is rider-managed
                                            if (isDelivery) {
                                              return optionValue === 5
                                            }
                                            return (
                                              optionValue === 3 ||
                                              optionValue === 5
                                            )
                                          case 3:
                                            // Delivery: Delivered (4) is rider-managed
                                            if (isDelivery) {
                                              return optionValue === 5
                                            }
                                            return (
                                              optionValue === 4 ||
                                              optionValue === 5
                                            )
                                          case 4:
                                            return false
                                          case 5:
                                            return false
                                          default:
                                            return false
                                        }
                                      })
                                      .map(option => ({
                                        ...option,
                                        label: getSystemOrderStatusLabel(
                                          option.value,
                                          item.orderType,
                                          t
                                        ),
                                        isDisabled:
                                          parseInt(option.value) ===
                                          resolveSystemOrderStatusValue(item),
                                      }))}
                                    placeholder={t("Update Status")}
                                    noOptionsMessage={() => t("No options")}
                                    {...ORDER_STATUS_SELECT_MENU_PROPS}
                                    styles={orderStatusSelectStyles}
                                  />
                                )}
                              </th>
                            )}
                            <td
                              style={{ textAlign: "center" }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <i
                                id={`download_order_pdf_${item._id}`}
                                className={`mdi ${
                                  downloadingPdfId === String(item._id)
                                    ? "mdi-loading mdi-spin"
                                    : "mdi-download"
                                } font-size-18 text-primary cursor-pointer`}
                                onClick={e =>
                                  handleDownloadOrderPdf(item._id, e)
                                }
                              ></i>
                              <UncontrolledTooltip
                                placement="top"
                                target={`download_order_pdf_${item._id}`}
                              >
                                {t("Download Order Details PDF")}
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && orders.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={orders}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal size="md" isOpen={showModal} centered={true}>
        <div className="modal-content">
          <ModalBody className="px-4 py-5 text-center">
            <button
              type="button"
              onClick={() => {
                setShowModal(false)
                setSelectedId(null)
              }}
              className="btn-close position-absolute end-0 top-0 m-3"
            ></button>
            <div className="avatar-sm mb-4 mx-auto">
              <div className="avatar-title bg-primary text-primary bg-opacity-10 font-size-20 rounded-3">
                <i className="bx bx-sync"></i>
              </div>
            </div>
            <p className="text-muted font-size-16 mb-4">
              {t("Are you sure you want to Sync order with POS")}
            </p>

            <div className="hstack gap-2 justify-content-center mb-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false)
                  setSelectedId(null)
                }}
              >
                {t("No")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSentOnPOS}
                disabled={isLoading}
                title={t("Sync Order With POS")}
              >
                {t("Yes")}
              </button>
            </div>
          </ModalBody>
        </div>
      </Modal>
      <AssignToRiderModal
        isOpen={showAssignRiderModal}
        toggle={() => setShowAssignRiderModal(false)}
        selectedOrderIds={selectedOrderIds}
        selectedOrders={selectedOrders}
        branchsList={branchsList}
        onSuccess={handleAssignSuccess}
      />
      <AssignToBranchModal
        isOpen={showAssignBranchModal}
        toggle={() => setShowAssignBranchModal(false)}
        selectedOrderIds={selectedOrderIds}
        selectedOrders={selectedOrders}
        branchsList={branchsList}
        onSuccess={handleAssignSuccess}
      />
    </div>
  )
}

export default Orders
